import { LocaleSwitcher, useT } from '@planetra/i18n';
import { Logo } from '@planetra/ui';
import { Outlet } from 'react-router';

import { Topography } from './Topography';

/* Brand DNA §9 hexes (the `--pl-c-forest/teal/mint` primitives in the UI kit's
   tokens.css). The shader takes hex, not a CSS variable, so they are repeated
   here: low ground in forest, the middle in teal, ridges in mint. */
const CONTOUR_LOW = '#173e35';
const CONTOUR_MID = '#247d70';
const CONTOUR_HIGH = '#adf0b1';

/**
 * The shell the signed-out screens render inside (P0-WEB-06).
 *
 * Separate from `AppLayout` for one substantive reason: `AppLayout` opens the
 * Reverb socket, and a visitor who has not signed in has no organisation to
 * subscribe to and no session to authorise a private channel with. Sharing the
 * shell would hold an idle socket open on every sign-in page and put a
 * connection indicator on a screen where nothing is live by definition.
 *
 * The locale switcher is here because it has to be: PRD §3.3 requires EN/MS
 * across the portal, and a planner who reads Malay needs the toggle before
 * they sign in, not after.
 */
export function AuthLayout() {
  const { t } = useT();

  return (
    <div className="relative isolate flex min-h-screen flex-col bg-canvas text-fg">
      {/* Contour lines behind every signed-out screen (P1-WEB-35): a planning
          platform's front door drawn as a moving topographic map. Decorative,
          so hidden from assistive tech, and still under reduced motion. */}
      <div aria-hidden="true" className="fixed inset-0 -z-10">
        <Topography
          lowColor={CONTOUR_LOW}
          midColor={CONTOUR_MID}
          highColor={CONTOUR_HIGH}
          speed={0.35}
          morphAmount={3.0}
          morphSpeed={0.05}
          bands={2.0}
          thickness={0.01}
          glow={0.16}
          contrast={3.0}
          opacity={0.8}
          grainIntensity={0.018}
          mouseRadius={0.3}
          mouseStrength={0.4}
        />
      </div>

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-(--pl-z-toast) focus:rounded-md focus:bg-surface-raised focus:px-3 focus:py-2 focus:text-sm"
      >
        {t('common:skipToContent')}
      </a>

      <header className="flex h-(--pl-header-height) shrink-0 items-center justify-between px-4">
        <Logo label={t('common:appName')} className="h-5" />
        <LocaleSwitcher className="w-44" />
      </header>

      {/* Centred, and capped rather than centred vertically on tall screens:
          a form that sits mid-viewport on a 27-inch monitor puts the first
          field below where the eye lands. */}
      <main id="main" className="flex min-h-0 flex-1 justify-center px-4 py-8 sm:py-16">
        <div className="w-full max-w-sm">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
